import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Users, Package, Calendar, Download, Filter, RefreshCw } from 'lucide-react';
import { salesService } from '../services/salesService';

interface ReportData {
  totalRevenue: number;
  totalSales: number;
  totalUsers: number;
  totalProducts: number;
  monthlyRevenue: { month: string; revenue: number }[];
  topProducts: { name: string; sales: number; revenue: number }[];
  salesByStatus: { status: string; count: number; percentage: number }[];
}

const ReportsPage: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [reportType, setReportType] = useState('overview');

  const loadReportData = async () => {
    try {
      setLoading(true);
      
      // Simular carregamento de dados de diferentes serviços
      const [salesStats, sales] = await Promise.all([
        salesService.getSalesStats(),
        salesService.getAll()
      ]);
      
      // Dados simulados para usuários e produtos
      const users = Array.from({ length: 25 }, (_, i) => ({ id: i + 1, name: `Usuário ${i + 1}` }));
      const products = Array.from({ length: 15 }, (_, i) => ({ id: i + 1, name: `Produto ${i + 1}` }));

      // Dados simulados para gráficos
      const monthlyRevenue = [
        { month: 'Jan', revenue: 12500 },
        { month: 'Fev', revenue: 15200 },
        { month: 'Mar', revenue: 18900 },
        { month: 'Abr', revenue: 16700 },
        { month: 'Mai', revenue: 21300 },
        { month: 'Jun', revenue: 19800 }
      ];

      const topProducts = [
        { name: 'Produto A', sales: 145, revenue: 7250 },
        { name: 'Produto B', sales: 98, revenue: 2940 },
        { name: 'Produto C', sales: 76, revenue: 5700 },
        { name: 'Produto D', sales: 54, revenue: 1350 },
        { name: 'Produto E', sales: 32, revenue: 4800 }
      ];

      const completedSales = sales.filter(sale => sale.status === 'completed').length;
      const pendingSales = sales.filter(sale => sale.status === 'pending').length;
      const cancelledSales = sales.filter(sale => sale.status === 'cancelled').length;
      const totalSalesCount = sales.length;

      const salesByStatus = [
        { 
          status: 'Concluídas', 
          count: completedSales, 
          percentage: totalSalesCount > 0 ? (completedSales / totalSalesCount) * 100 : 0 
        },
        { 
          status: 'Pendentes', 
          count: pendingSales, 
          percentage: totalSalesCount > 0 ? (pendingSales / totalSalesCount) * 100 : 0 
        },
        { 
          status: 'Canceladas', 
          count: cancelledSales, 
          percentage: totalSalesCount > 0 ? (cancelledSales / totalSalesCount) * 100 : 0 
        }
      ];

      setReportData({
        totalRevenue: salesStats.totalRevenue,
        totalSales: salesStats.totalSales,
        totalUsers: users.length,
        totalProducts: products.length,
        monthlyRevenue,
        topProducts,
        salesByStatus
      });
    } catch (error) {
      console.error('Erro ao carregar dados do relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [dateRange, reportType]);

  const exportReport = () => {
    // Simular exportação de relatório
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span>Carregando relatórios...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Relatórios
          </h1>
          <p className="text-gray-600 mt-1">Análise detalhada do desempenho do sistema</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadReportData}
            className="berry-button-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
          <button
            onClick={exportReport}
            className="berry-button-primary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="berry-card p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Período
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="berry-input w-full lg:w-auto"
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="365">Último ano</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Relatório
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="berry-input w-full lg:w-auto"
            >
              <option value="overview">Visão Geral</option>
              <option value="sales">Vendas</option>
              <option value="products">Produtos</option>
              <option value="users">Usuários</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="berry-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Receita Total</p>
              <p className="text-2xl font-bold text-gray-900">
                R$ {reportData?.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-green-600 mt-1">+12.5% vs mês anterior</p>
            </div>
            <div className="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-berry">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="berry-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Vendas</p>
              <p className="text-2xl font-bold text-gray-900">{reportData?.totalSales}</p>
              <p className="text-sm text-blue-600 mt-1">+8.2% vs mês anterior</p>
            </div>
            <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-berry">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="berry-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{reportData?.totalUsers}</p>
              <p className="text-sm text-purple-600 mt-1">+15.3% vs mês anterior</p>
            </div>
            <div className="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-berry">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="berry-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Produtos Cadastrados</p>
              <p className="text-2xl font-bold text-gray-900">{reportData?.totalProducts}</p>
              <p className="text-sm text-orange-600 mt-1">+5.1% vs mês anterior</p>
            </div>
            <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-berry">
              <Package className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <div className="berry-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Receita Mensal</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {reportData?.monthlyRevenue.map((item, index) => {
              const maxRevenue = Math.max(...reportData.monthlyRevenue.map(r => r.revenue));
              const percentage = (item.revenue / maxRevenue) * 100;
              return (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 text-sm font-medium text-gray-600">{item.month}</div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-20 text-sm font-medium text-gray-900 text-right">
                    R$ {item.revenue.toLocaleString('pt-BR')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sales by Status */}
        <div className="berry-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Vendas por Status</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {reportData?.salesByStatus.map((item, index) => {
              const colors = ['bg-green-500', 'bg-yellow-500', 'bg-red-500'];
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${colors[index]}`}></div>
                    <span className="text-sm font-medium text-gray-700">{item.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{item.count} vendas</span>
                    <span className="text-sm font-medium text-gray-900">
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Products Table */}
      <div className="berry-card overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Produtos Mais Vendidos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendas
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receita
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData?.topProducts.map((product, index) => {
                const maxSales = Math.max(...reportData.topProducts.map(p => p.sales));
                const percentage = (product.sales / maxSales) * 100;
                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-berry flex items-center justify-center">
                          <span className="text-white text-sm font-medium">{index + 1}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{product.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.sales}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      R$ {product.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{percentage.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;